import React, { Suspense, lazy, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Toaster } from 'react-hot-toast';
import { App as CapacitorApp } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { NativeProvider } from '../utils/NativeContext';
import { SocketProvider } from '../utils/SocketContext';
import { NotificationCountProvider } from '../utils/NotificationCountContext';
import { MessageCountProvider } from '../utils/MessageCountContext';
import AppIconBadgeBridge from './Layout/AppIconBadgeBridge';
import { setUser, setLoading, setProfileComplete } from '../store/auth.reducer';
import { selectIsAuthenticated, selectAuthLoading, selectProfileComplete } from '../store/auth.reducer';
import Loading from './Loading/Loading';
import AppLayout from './Layout/AppLayout';
import ShareSurfaceLayout from './Layout/ShareSurfaceLayout';
import { getOAuthBackendOrigin } from '../helpers/oauthHelpers';

const Home = lazy(() => import('../routes/Home'));
const Login = lazy(() => import('../routes/Login'));
const Register = lazy(() => import('../routes/Register'));
const ForgotPassword = lazy(() => import('../routes/ForgotPassword'));
const CompleteProfile = lazy(() => import('../routes/CompleteProfile'));
const Profile = lazy(() => import('../routes/Profile'));
const Terms = lazy(() => import('../routes/Legal/Terms'));
const Privacy = lazy(() => import('../routes/Legal/Privacy'));
const Support = lazy(() => import('../routes/Support'));
const Messages = lazy(() => import('../routes/Messages'));
const Clubs = lazy(() => import('../routes/Clubs'));
const Events = lazy(() => import('../routes/Events'));
const Notifications = lazy(() => import('../routes/Notifications'));
const Post = lazy(() => import('../routes/Post'));
const Store = lazy(() => import('../routes/Store'));
const Orders = lazy(() => import('../routes/Orders'));
const PostcardClaim = lazy(() => import('../routes/PostcardClaim'));
const Search = lazy(() => import('../routes/Search'));
const NotFound = lazy(() => import('../routes/NotFound'));

const RootRoute = () => {
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const profileComplete = useSelector(selectProfileComplete);
  const loading = useSelector(selectAuthLoading);
  const location = useLocation();

  if (loading) return <Loading />;
  if (!isAuthenticated) {
    if (location.pathname !== '/') return <Navigate to="/" replace />;
    return <Login />;
  }
  if (!profileComplete) return <Navigate to="/complete-profile" replace />;
  return <AppLayout />;
};

const PublicRoute = ({ children }) => {
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const loading = useSelector(selectAuthLoading);

  if (loading) return <Loading />;
  if (isAuthenticated) return <Navigate to="/" replace />;
  return children;
};

/** OAuth complete-profile: allow when ?provider=google|apple (OAuth callback) or authenticated but incomplete (legacy) */
const OAuthCompleteProfileRoute = ({ children }) => {
  const [searchParams] = useSearchParams();
  const provider = searchParams.get('provider');
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const profileComplete = useSelector(selectProfileComplete);
  const loading = useSelector(selectAuthLoading);

  if (loading) return <Loading />;
  if (isAuthenticated && profileComplete) return <Navigate to="/" replace />;
  const hasProvider = provider === 'google' || provider === 'apple';
  if (!hasProvider && !isAuthenticated) return <Navigate to="/" replace />;
  return children;
};

// iOS replays cold-start launch URLs until the process exits; track handled
// handoffs so getLaunchUrl() does not re-navigate on every effect cycle.
const processedOAuthHandoffs = new Set();
let hasCheckedNativeLaunchUrl = false;

function App() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const pathname = window.location.pathname;
  const search = window.location.search;
  const isOAuthCallbackPath =
    pathname === '/api/auth/google/callback' || pathname === '/api/auth/apple/callback';

  useEffect(() => {
    if (!isOAuthCallbackPath) return;
    const backendOrigin = getOAuthBackendOrigin();
    if (!backendOrigin || window.location.origin === backendOrigin) return;
    window.location.replace(`${backendOrigin}${pathname}${search}`);
  }, [isOAuthCallbackPath, pathname, search]);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return undefined;

    let appUrlOpenListener;

    const handleNativeUrl = async (event) => {
      if (!event?.url) return;
      let url;
      try {
        url = new URL(event.url);
      } catch {
        return;
      }
      if (url.protocol.toLowerCase() !== 'aurasphere:') return;

      const params = new URLSearchParams(url.search);
      const handoffToken = params.get('oauth_handoff');
      if (handoffToken) {
        if (processedOAuthHandoffs.has(handoffToken)) {
          return;
        }
        processedOAuthHandoffs.add(handoffToken);

        try {
          const response = await fetch('/api/auth/native-session-handoff', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ oauth_handoff: handoffToken }),
            credentials: 'include',
          });
          const data = await response.json();
          if (response.ok && data.user) {
            dispatch(
              setUser({
                ...data.user,
                has_password: data.has_password ?? !!data.user?.password,
              })
            );
            dispatch(setProfileComplete(data.isProfileComplete ?? true));
          }
          if (response.ok) {
            try {
              await Browser.close();
            } catch {
              // The handoff has already completed; closing the auth browser is best-effort.
            }
          }
        } catch (error) {
          console.error('Error completing native OAuth handoff:', error);
          return;
        } finally {
          params.delete('oauth_handoff');
        }
      }

      const path = url.pathname && url.pathname.startsWith('/') ? url.pathname : '/';
      const remainingSearch = params.toString();
      navigate(`${path}${remainingSearch ? `?${remainingSearch}` : ''}`, { replace: true });
    };

    CapacitorApp.addListener('appUrlOpen', handleNativeUrl).then((listener) => {
      appUrlOpenListener = listener;
    });
    if (!hasCheckedNativeLaunchUrl) {
      hasCheckedNativeLaunchUrl = true;
      CapacitorApp.getLaunchUrl().then((launchUrl) => {
        if (launchUrl) handleNativeUrl(launchUrl);
      });
    }

    return () => {
      if (appUrlOpenListener) appUrlOpenListener.remove();
    };
  }, [dispatch, navigate]);

  useEffect(() => {
    if (isOAuthCallbackPath) return;
    const checkAuth = async () => {
      dispatch(setLoading(true));
      try {
        const response = await fetch('/api/auth/me', { credentials: 'include' });
        if (response.ok) {
          const data = await response.json();
          dispatch(
            setUser({
              ...data.user,
              has_password: data.has_password ?? !!data.user?.password,
            })
          );
          dispatch(setProfileComplete(data.isProfileComplete ?? true));
        } else {
          dispatch(setUser(null));
          dispatch(setProfileComplete(null));
        }
      } catch {
        dispatch(setUser(null));
        dispatch(setProfileComplete(null));
      } finally {
        dispatch(setLoading(false));
      }
    };
    checkAuth();
  }, [dispatch, isOAuthCallbackPath]);

  if (isOAuthCallbackPath) {
    return <Loading />;
  }

  return (
    <NativeProvider>
      <SocketProvider>
        <MessageCountProvider>
          <NotificationCountProvider>
            <AppIconBadgeBridge />
      <div className="h-dvh overflow-hidden flex flex-col bg-void">
        {/* Push toasts below the iOS status bar (safe-area-inset-top) so the
            in-app banner is fully visible on notched devices. The default
            16px top from `position="top-center"` lands behind the status bar
            in Capacitor's edge-to-edge WebView. */}
        <Toaster
          position="top-center"
          containerStyle={{ top: 'calc(env(safe-area-inset-top) + 1rem)' }}
        />
        <div className="flex-1 min-h-0 overflow-hidden">
          <Suspense fallback={<Loading />}>
          <Routes>
            <Route path="/login" element={<Navigate to="/" replace />} />
            <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
            <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
            <Route path="/complete-profile" element={<OAuthCompleteProfileRoute><CompleteProfile /></OAuthCompleteProfileRoute>} />
            <Route path="/tc" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/support" element={<Support />} />
            <Route path="/posts/:id" element={<ShareSurfaceLayout />}>
              <Route index element={<Post />} />
            </Route>
            <Route path="/profile/:username" element={<ShareSurfaceLayout />}>
              <Route index element={<Profile />} />
            </Route>
            <Route path="/" element={<RootRoute />}>
              <Route index element={<Home />} />
              <Route path="home" element={<Home />} />
              <Route path="profile" element={<Profile />} />
              <Route path="messages" element={<Messages />} />
              <Route path="messages/group/:id" element={<Messages />} />
              <Route path="messages/:id" element={<Messages />} />
              <Route path="clubs" element={<Clubs />} />
              <Route path="clubs/:id" element={<Clubs />} />
              <Route path="notifications" element={<Notifications />} />
              <Route path="search" element={<Search />} />
              <Route path="events" element={<Events />} />
              <Route path="events/:id" element={<Events />} />
              <Route path="postcard/claim" element={<PostcardClaim />} />
              <Route path="store/orders" element={<Orders />} />
              <Route path="store/:id" element={<Store />} />
              <Route path="store" element={<Store />} />
            </Route>
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
        </div>
      </div>
          </NotificationCountProvider>
        </MessageCountProvider>
      </SocketProvider>
    </NativeProvider>
  );
}

export default App;

/** @internal test-only reset for module-level OAuth handoff dedup state */
export function __resetNativeOAuthHandoffStateForTests() {
  processedOAuthHandoffs.clear();
  hasCheckedNativeLaunchUrl = false;
}
